import * as THREE from 'three';
import { FOG_DENSITY, SCENE_BG } from '../../constants';

export const DEPTH_VERT = /* glsl */ `
  varying float vFogDepth;
`;

export const DEPTH_VERT_BODY = /* glsl */ `
  vFogDepth = -mvPosition.z;
`;

export const DEPTH_FRAG = /* glsl */ `
  uniform vec3 uFogColor;
  uniform float uFogDensity;
  varying float vFogDepth;
  void applyVoidFog() {
    float fogFactor = 1.0 - exp(-uFogDensity * uFogDensity * vFogDepth * vFogDepth);
    gl_FragColor.rgb = mix(gl_FragColor.rgb, uFogColor, clamp(fogFactor, 0.0, 1.0));
  }
`;

export const NODE_VERT = /* glsl */ `
  varying vec2 vUv;
  ${DEPTH_VERT}
  void main() {
    vUv = uv;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    ${DEPTH_VERT_BODY}
  }
`;

export const NODE_FRAG = /* glsl */ `
  uniform vec3 uColor;
  uniform vec3 uEmissive;
  uniform float uEmissiveIntensity;
  uniform float uOpacity;
  varying vec2 vUv;
  ${DEPTH_FRAG}
  void main() {
    // Flat disk — the Obsidian / second-brain circle, not a glass sphere.
    float r = length(vUv - 0.5) * 2.0;
    if (r > 1.0) discard;
    float core = 1.0 - smoothstep(0.0, 0.88, r);
    float rim = 1.0 - smoothstep(0.78, 1.0, r);
    vec3 body = uColor * (0.62 + core * 0.45);
    vec3 glow = uEmissive * uEmissiveIntensity * core * 0.28;
    gl_FragColor = vec4(body + glow, uOpacity * rim);
    applyVoidFog();
  }
`;

export const DUST_VERT = /* glsl */ `
  attribute float aSize;
  attribute float aOpacity;
  uniform float uPixelRatio;
  varying float vOpacity;
  ${DEPTH_VERT}
  void main() {
    vOpacity = aOpacity;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    gl_PointSize = aSize * uPixelRatio * (110.0 / max(-mvPosition.z, 8.0));
    ${DEPTH_VERT_BODY}
  }
`;

export const DUST_FRAG = /* glsl */ `
  varying float vOpacity;
  ${DEPTH_FRAG}
  void main() {
    vec2 p = gl_PointCoord * 2.0 - 1.0;
    float d = dot(p, p);
    if (d > 1.0) discard;
    float glow = exp(-d * 3.6);
    gl_FragColor = vec4(vec3(0.7, 0.78, 0.95) * glow, glow * vOpacity);
    applyVoidFog();
  }
`;

export function voidFogUniforms() {
  return {
    uFogColor: { value: new THREE.Color(SCENE_BG) },
    uFogDensity: { value: FOG_DENSITY },
  };
}

export function bridgeLooks(mat, { color, emissive, emissiveIntensity, opacity }) {
  const u = mat.uniforms;
  if (u.uColor) u.uColor.value.copy(color);
  if (u.uEmissive && emissive) u.uEmissive.value.copy(emissive);
  if (u.uEmissiveIntensity) u.uEmissiveIntensity.value = emissiveIntensity ?? 0;
  if (u.uOpacity) u.uOpacity.value = opacity ?? 0;

  Object.defineProperty(mat, 'color', {
    get() { return u.uColor.value; },
    set(v) { u.uColor.value.copy(v); },
    configurable: true,
  });
  Object.defineProperty(mat, 'opacity', {
    get() { return u.uOpacity.value; },
    set(v) { u.uOpacity.value = v; },
    configurable: true,
  });
  if (u.uEmissive) {
    Object.defineProperty(mat, 'emissive', {
      get() { return u.uEmissive.value; },
      set(v) { u.uEmissive.value.copy(v); },
      configurable: true,
    });
    Object.defineProperty(mat, 'emissiveIntensity', {
      get() { return u.uEmissiveIntensity.value; },
      set(v) { u.uEmissiveIntensity.value = v; },
      configurable: true,
    });
  }
  return mat;
}
